import { observer } from 'mobx-react';
import classnames from 'classnames';

import styles from './FileAsset.module.scss';

import { FixedIcon, Icon, IconType } from '@components/Icon';
import { ConfirmPopup } from '@components/Popup';
import { FileAsset as StoreFileAsset } from '@store/models/FileAsset';
import { TextTruncate } from '@components/TextTruncate';
import { store } from '@store';
import { translate } from '@chaika/i18n/helper';

import { useFileAsset } from './hooks/useFileAsset';

interface Props {
    asset: StoreFileAsset;
    onDeleteCLick(fileName: string): void;
}

export const FileAsset = observer(({ asset, onDeleteCLick }: Props) => {
    const {
        isDeletingPopupShowing,
        onShowFileClick,
        onDownloadFileClick,
        onSharePacs,
        onDeleteClick,
        onConfirmDeleting,
        onCancelDeleting,
    } = useFileAsset({ asset, onDeleteCLick });

    const disableRemove = store.authUser.id !== asset.authorId && store.entry.isViewMode;

    const isPacsAsset = asset.path.includes('pacs.chaika.com');

    return (
        <div className={styles.root}>
            <div className={styles.background}>
                {FixedIcon.getDocument()}
                <div className={styles.extension}>{asset.extension}</div>

                <div
                    className={classnames([styles.button, styles.showButton])}
                    onClick={onShowFileClick}
                >
                    <Icon type={IconType.EYE} />
                </div>
                {isPacsAsset ? (
                    <div
                        className={classnames([styles.button, styles.downloadButton])}
                        onClick={onSharePacs}
                    >
                        <Icon type={IconType.OPEN_LINK} />
                    </div>
                ) : (
                    <div
                        className={classnames([styles.button, styles.downloadButton])}
                        onClick={onDownloadFileClick}
                    >
                        <Icon type={IconType.DOWNLOAD} />
                    </div>
                )}
                {!disableRemove && (
                    <div
                        className={classnames([styles.button, styles.deleteButton])}
                        onClick={onDeleteClick}
                    >
                        <Icon type={IconType.CROSS} />
                    </div>
                )}
            </div>
            <div
                className={styles.title}
                title={asset.title}
            >
                <TextTruncate
                    splitByLetters
                    text={asset.title}
                    maxLine={2}
                />
            </div>
            {isDeletingPopupShowing && (
                <ConfirmPopup
                    title={translate('deletingFile')}
                    description={`${translate('sureToDeleteFile')}: ${asset.name}`}
                    onConfirm={onConfirmDeleting}
                    onCancel={onCancelDeleting}
                />
            )}
        </div>
    );
});
